import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import axiosInstance from '../../config/axios.js';
import userService from '../../services/UserService.js';
import companyService from '../../services/CompanyService.js';
import { locationHubService } from '../../services/locationHubService.js';
import { formatLocationDropdownLabel, sortLocationsAsc } from '../../utils/locationDropdownLabel.js';
import { Users, Plus, X, MapPin, Camera } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import StateSelectField from '../common/StateSelectField.js';
import AgentPartySelect from './AgentPartySelect.js';
import AgentInternalFlags from './AgentInternalFlags.js';
import {
  PARTY_LINKED_ROLES,
  filterCreatableRoles,
} from '../../utils/userCreatableRoles.js';


const SALARY_TYPE_OPTIONS = [
  { value: 'fixed_monthly', label: 'Fixed Monthly Salary' },
  { value: 'daily_wage', label: 'Daily Wages' },
];

const getSalaryAmountLabel = (salaryType) =>
  salaryType === 'daily_wage' ? 'Daily Wage (₹)' : 'Fixed Monthly Salary (₹)';

const getSalaryAmountPlaceholder = (salaryType) =>
  salaryType === 'daily_wage' ? 'Enter daily wage amount' : 'Enter monthly salary';

const AddUser = () => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    email: '',
    contact: '',
    city: '',
    state: '',
    location: '',
    dob: '',
    document_type: 'aadhar',
    document_number: '',
    role_id: '',
    password: '',
    status: 'active',
    salary_type: 'fixed_monthly',
    monthly_salary: '',
    booking_commission: '',
    delivery_commission: '',
    company_id: '',
    party_id: '',
    location_hub_id: '',
    commission_enabled: false,
    is_pickup_man: false,
    tds_percentage: '0',
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [documentAttachment, setDocumentAttachment] = useState(null);
  const [documentPreview, setDocumentPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [drivingLicenseAttachment, setDrivingLicenseAttachment] = useState(null);
  const [drivingLicensePreview, setDrivingLicensePreview] = useState(null);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [locationHubs, setLocationHubs] = useState([]);
  const [sendEmail, setSendEmail] = useState(true);
  const [photoError, setPhotoError] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [licenseError, setLicenseError] = useState('');
  const [locationFormErrors, setLocationFormErrors] = useState({});
  
  // Location Hub Creation States
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [newLocationData, setNewLocationData] = useState({
    address: '',
    location: '',
    state: '',
    location_code: '',
    destination_type: 'hub_destination',
    location_hub_contact: '',
  });
  
  const navigate = useNavigate();

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    name:            (v) => validators.required(v, 'Name') || validators.textOnly(v, 'Name'),
    email:           (v) => validators.required(v, 'Email') || validators.email(v),
    contact:         (v) => validators.required(v, 'Contact') || validators.contact(v),
    username:        (v) => validators.required(v, 'Username') || validators.alphanumericDash(v, 'Username'),
    password:        (v) => validators.required(v, 'Password') || validators.password(v),
    document_number: (v) => validators.required(v, 'Document number') || validators.alphanumeric(v, 'Document number'),
    monthly_salary:  (v) => validators.number(v, { min: 0, label: 'Salary' }),
  };

  const documentTypes = ['aadhar', 'pan', 'driving_license', 'voter_id', 'passport'];
  const userStatuses = ['active', 'inactive'];

  // Check if user has permission to add users
  const canAddUser = ['System Admin', 'Admin', 'Accountant'].includes(currentUser?.role);
  const isSystemAdmin = currentUser?.role === 'System Admin';
  const isAdmin = currentUser?.role === 'Admin';

  useEffect(() => {
    if (!canAddUser) {
      Alert.error('You do not have permission to add users');
      navigate('/users');
      return;
    }

    fetchRoles();
    
    // Fetch companies only for System Admin
    if (isSystemAdmin) {
      fetchCompanies();
    } else {
      const userCompanyInternalId = currentUser?.company_internal_id;
      const userCompanyStringId = currentUser?.company_id;
      // Fallback to string company_id if internal ID is missing, so company_id stays truthy
      const companyIdToSet = userCompanyInternalId || userCompanyStringId;
      setFormData(prev => ({ ...prev, company_id: companyIdToSet }));
      if (userCompanyStringId) {
        fetchLocationHubs(userCompanyStringId);
      }
    }
    // Mount-only: roles/companies bootstrap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch location hubs when company changes
  useEffect(() => {
    if (formData.company_id) {
      if (isSystemAdmin) {
        const selectedCompany = companies.find(c => c.id === parseInt(formData.company_id));
        if (selectedCompany) {
          fetchLocationHubs(selectedCompany.company_id);
        }
      } else {
        // For Admin/Accountant: company_id is already set from currentUser,
        // pass the string company_id so fetchLocationHubs can filter correctly
        fetchLocationHubs(formData.company_id);
      }
    } else {
      setLocationHubs([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.company_id, companies, isSystemAdmin]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getActiveCompanies();
      setCompanies(response.data || []);
    } catch (err) {
      Alert.error('Failed to load companies');
      console.error('Failed to load companies', err);
    }
  };

  const fetchLocationHubs = async (companyId) => {
    try {
      const response = await locationHubService.getAllHubs(1, 1000, { 
        all: 'true',
        ...(companyId ? { company_id: companyId } : {}),
      });
      
      let hubs = response.data.hubs || [];
      
      setLocationHubs(hubs);
    } catch (err) {
      console.error('Failed to load location hubs', err);
      setLocationHubs([]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axiosInstance.get('/roles');
      let allRoles = response.data.data;
      
      // Filter roles based on current user's role
      setRoles(filterCreatableRoles(allRoles, currentUser));
    } catch (err) {
      Alert.error('Failed to load roles');
      console.error('Failed to load roles', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'contact') {
      filtered = inputFilters.digits10(value);
      setFormData((prev) => ({
        ...prev,
        contact: filtered,
        username: (!prev.username || prev.username === prev.contact) ? filtered : prev.username,
      }));
      if (validationRules.contact) validateField('contact', filtered, validationRules.contact);
      if ((!formData.username || formData.username === formData.contact) && validationRules.username) {
        validateField('username', filtered, validationRules.username);
      }
      return;
    }
    else if (name === 'name') filtered = inputFilters.lettersOnly(value);
    else if (name === 'monthly_salary') filtered = inputFilters.amount(value);
    else if (name === 'document_number') filtered = inputFilters.alphanumeric(value);

    if (name === 'city') {
      const hub = locationHubs.find((h) => h.location === value);
      setFormData((prev) => ({
        ...prev,
        city: value,
        state: hub?.state || prev.state,
        location_hub_id: hub?.id != null ? String(hub.id) : '',
      }));
      return;
    }

    if (name === 'role_id') {
      const role = roles.find((r) => String(r.id) === String(filtered));
      setFormData((prev) => ({
        ...prev,
        role_id: filtered,
        party_id: PARTY_LINKED_ROLES.includes(role?.role_name) ? prev.party_id : '',
        commission_enabled: role?.role_name === 'Agent' ? prev.commission_enabled : false,
        is_pickup_man: role?.role_name === 'Agent' ? prev.is_pickup_man : false,
        tds_percentage: role?.role_name === 'Agent' ? prev.tds_percentage : '0',
      }));
      return;
    }

    if (name === 'party_id') {
      setFormData((prev) => ({
        ...prev,
        party_id: filtered,
        commission_enabled: filtered ? true : prev.commission_enabled,
      }));
      return;
    }

    if (e.target?.type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleProfilePhotoChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 1024 * 1024) {
        setPhotoError('Profile photo must be less than 1MB');
        return;
      }
      setPhotoError('');
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDocumentAttachmentChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setDocumentError('Document must be less than 2MB');
        return;
      }
      setDocumentError('');
      setDocumentAttachment(file);
      if (file.type === 'application/pdf') {
        setDocumentPreview('pdf');
      } else {
        const reader = new FileReader();
        reader.onloadend = () => setDocumentPreview(reader.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleDrivingLicenseChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setLicenseError('Driving license must be less than 2MB');
        return;
      }
      setLicenseError('');
      setDrivingLicenseAttachment(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setDrivingLicensePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDrivingLicense = () => {
    setDrivingLicenseAttachment(null);
    setDrivingLicensePreview(null);
    const fileInput = document.getElementById('driving-license-input');
    if (fileInput) fileInput.value = '';
  };

  // Location Hub Creation Functions
  const handleNewLocationChange = (e) => {
    const { name, value } = e.target;
    setNewLocationData((prev) => ({ ...prev, [name]: value }));
    setLocationFormErrors((prev) => ({ ...prev, [name]: '', submit: '', company: '' }));
  };

  const validateLocationForm = () => {
    const nextErrors = {};
    if (!newLocationData.address.trim()) nextErrors.address = 'Location address is required';
    if (!newLocationData.location.trim()) nextErrors.location = 'Location name is required';
    if (!newLocationData.state) nextErrors.state = 'State is required';
    if (!newLocationData.location_code.trim()) nextErrors.location_code = 'Location code is required';
    const hubContact = (newLocationData.location_hub_contact || '').trim();
    if (!hubContact) nextErrors.location_hub_contact = 'Contact number is required';
    else if (!/^\d{10}$/.test(hubContact)) nextErrors.location_hub_contact = 'Enter a 10 digit contact number';
    setLocationFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleAddLocation = async () => {
    if (!validateLocationForm()) {
      return;
    }

    if (!formData.company_id) {
      setLocationFormErrors((prev) => ({ ...prev, company: 'Please select a company first' }));
      return;
    }

    try {
      setAddingLocation(true);
      
      // Get the selected company's internal ID (database primary key)
      let targetCompanyInternalId = null;
      
      if (isSystemAdmin) {
        const selectedCompany = companies.find(c => c.id === parseInt(formData.company_id));
        if (selectedCompany) {
          targetCompanyInternalId = selectedCompany.id; // This is the internal database ID
        }
      } else {
        // For Admin/Accountant, get their company's internal ID from currentUser
        targetCompanyInternalId = currentUser?.company_internal_id || null;
      }
      
      const submitData = {
        address: newLocationData.address.trim(),
        location: newLocationData.location.trim(),
        state: newLocationData.state,
        location_code: newLocationData.location_code.trim().toUpperCase(),
        destination_type: newLocationData.destination_type,
        location_hub_contact: newLocationData.location_hub_contact.trim(),
      };

      // Pass company_id (internal database ID) for both System Admin and Admin
      const response = await locationHubService.createHub(
        submitData, 
        targetCompanyInternalId
      );
      
      Alert.success('Location hub created successfully!');
      
      // Reset form
      setNewLocationData({
        address: '',
        location: '',
        state: '',
        location_code: '',
        destination_type: 'hub_destination',
        location_hub_contact: '',
      });
      setShowAddLocation(false);
      
      // Get the newly created hub from response
      const newHub = response.data.hub;
      
      // Refresh location hubs list for the selected company
      await fetchLocationHubs(formData.company_id);
      
      // Auto-select the newly created location in Branch dropdown
      if (newHub && newHub.location) {
        setFormData(prev => ({
          ...prev,
          city: newHub.location,
          branch: newHub.location,
          state: newHub.state || prev.state,
          location_hub_id: newHub.id != null ? String(newHub.id) : '',
        }));
      }
      
    } catch (error) {
      console.error('Error creating location hub:', error);
      setLocationFormErrors({
        submit: error.response?.data?.error || 'Failed to create location hub',
      });
    } finally {
      setAddingLocation(false);
    }
  };

  const cancelAddLocation = () => {
    setShowAddLocation(false);
    setLocationFormErrors({});
    setNewLocationData({
      address: '',
      location: '',
      state: '',
      location_code: '',
      destination_type: 'hub_destination',
      location_hub_contact: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.modal('Please fix the highlighted errors before submitting', 'Cannot create user');
      return;
    }

    setLoading(true);

    if (!formData.company_id) {
      Alert.modal('Please select a company', 'Cannot create user');
      setLoading(false);
      return;
    }

    try {
      const userData = { ...formData };
      if (!userData.username && userData.contact) {
        userData.username = userData.contact;
      }
      const selectedRole = roles.find((r) => String(r.id) === String(formData.role_id));
      if (selectedRole?.role_name === 'Contract Party Admin' && !userData.party_id) {
        Alert.modal('Contract party is required for Contract Party Admin', 'Cannot create user');
        setLoading(false);
        return;
      }
      if (selectedRole?.role_name === 'Hub Admin' && !userData.location_hub_id) {
        Alert.modal('Hub/Branch assignment is required for Hub Admin', 'Cannot create user');
        setLoading(false);
        return;
      }
      if (!PARTY_LINKED_ROLES.includes(selectedRole?.role_name) || !userData.party_id) {
        delete userData.party_id;
      }
      if (selectedRole?.role_name === 'Agent') {
        userData.commission_enabled = Boolean(formData.commission_enabled);
        userData.is_pickup_man = Boolean(formData.is_pickup_man);
        userData.tds_percentage = formData.tds_percentage === '' ? 0 : Number(formData.tds_percentage);
      } else {
        delete userData.commission_enabled;
        delete userData.is_pickup_man;
        delete userData.tds_percentage;
      }
      if (!userData.location_hub_id) {
        delete userData.location_hub_id;
      }
      const response = await userService.create(userData, profilePhoto, documentAttachment, drivingLicenseAttachment);
      
      if (sendEmail && response.data) {
        try {
          const userId = response.data.user_id || response.data.id || response.data.username;
          
          if (userId) {
            await userService.sendCredentials(userId);
            Alert.success('User created and credentials sent successfully!');
          } else {
            Alert.success('User created successfully!');
          }
        } catch (emailErr) {
          Alert.success('User created successfully!');
          console.error('Failed to send credentials email:', emailErr);
        }
      } else {
        Alert.success('User created successfully!');
      }
      
      setTimeout(() => navigate('/users'), 1500);
    } catch (err) {
      Alert.modal(err.response?.data?.error || 'Failed to create user', 'Cannot create user');
    } finally {
      setLoading(false);
    }
  };

  // Get company name for display (for non-System Admin users)
  const getCompanyName = () => {
    if (isSystemAdmin) return '';
    return currentUser?.company_name || '';
  };

  return (
    <DashboardLayout>
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-0">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-primary flex items-center gap-2">
                    <Users className="w-6 h-6 sm:w-7 sm:h-7" />
                    Add New User
                  </h1>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">Create a new user account</p>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                  
                  {/* Company Selection - Only for System Admin */}
                  {isSystemAdmin && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Company Information
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                        <div className="col-span-2 sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="company_id"
                            value={formData.company_id}
                            onChange={handleChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          >
                            <option value="">Select Company</option>
                            {companies.map((company) => (
                              <option key={company.company_id} value={company.id}>
                                {company.company_name} ({company.company_id})
                              </option>
                            ))}
                          </SelectField>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Company Display - For Admin/Accountant (Read-only) */}
                  {!isSystemAdmin && (
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        Company Information
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                        <div className="col-span-2 sm:col-span-2">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company
                          </label>
                          <input
                            type="text"
                            value={getCompanyName()}
                            disabled
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-100 text-gray-600 text-sm sm:text-base cursor-not-allowed"
                          />
                          <p className="text-xs text-gray-500 mt-1">Users will be created under your company</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Section 1: Basic Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Basic Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Username <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="username"
                          value={formData.username}
                          onChange={handleChange}
                          placeholder="Same as contact if left empty"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.username ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.username} />
                        <p className="text-xs text-gray-500 mt-1">Filled from contact number unless you type a different username</p>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Full Name <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter full name"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.name} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          name="dob"
                          value={formData.dob}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Contact Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Contact Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Email <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="Enter email"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.email} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Contact Number <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="tel"
                          name="contact"
                          value={formData.contact}
                          onChange={handleChange}
                          placeholder="Enter 10-digit number"
                          maxLength={10}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.contact ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.contact} />
                      </div>

                      {/* Branch with Add Location Feature */}
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Branch <span className="text-danger font-bold">*</span>
                        </label>
                        <div className="flex gap-2">
                          <SelectField variant="form"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                            disabled={!formData.company_id}
                          >
                            <option value="">
                              {!formData.company_id 
                                ? 'Select company first' 
                                : locationHubs.length === 0 
                                  ? 'No locations available' 
                                  : 'Select Branch'}
                            </option>
                            {sortLocationsAsc(locationHubs).map((hub) => (
                              <option key={hub.hub_id} value={hub.location}>
                                {formatLocationDropdownLabel(hub)}
                              </option>
                            ))}
                          </SelectField>
                          
                          {/* Add Location Button - Only for System Admin and Admin */}
                          {(isSystemAdmin || isAdmin) && formData.company_id && (
                            <Button
                              icon={Plus}
                              onClick={() => {
                                setNewLocationData((prev) => ({
                                  ...prev,
                                  location_hub_contact: prev.location_hub_contact || formData.contact || '',
                                }));
                                setShowAddLocation(true);
                              }}
                              title="Add new location"
                            />
                          )}
                        </div>
                        {!formData.company_id && (
                          <p className="text-xs text-gray-500 mt-1">Please select a company first</p>
                        )}
                        {formData.company_id && locationHubs.length === 0 && !showAddLocation && (
                          <p className="text-xs text-orange-600 mt-1">
                            {(isSystemAdmin || isAdmin)
                              ? 'No locations found. Click + to add a new location.' 
                              : 'No locations available for this company.'}
                          </p>
                        )}
                      </div>

                      <StateSelectField
                        label="State"
                        value={formData.state}
                        onChange={(state) => setFormData((prev) => ({ ...prev, state }))}
                      />

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Location <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          placeholder="Enter location/address"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        />
                      </div>
                    </div>

                    {/* Add Location Form - Inline (Only for System Admin and Admin) */}
                    {showAddLocation && (isSystemAdmin || isAdmin) && (
                      <div className="mt-6 p-4 border-2 border-blue-200 bg-blue-50 rounded-xl">
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Add New Location Hub for {isSystemAdmin 
                              ? companies.find(c => c.id === parseInt(formData.company_id))?.company_name 
                              : currentUser?.company_name}
                          </h4>
                          <Button variant="ghost" size="sm" onClick={cancelAddLocation}>
                            <X className="w-5 h-5" />
                          </Button>
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4">
                          {/* Address */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="address"
                              value={newLocationData.address}
                              onChange={handleNewLocationChange}
                              placeholder="Enter address"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={addingLocation}
                            />
                            {locationFormErrors.address && (
                              <p className="text-xs text-red-600 mt-1">{locationFormErrors.address}</p>
                            )}
                          </div>

                          {/* Location Name */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Location Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="location"
                              value={newLocationData.location}
                              onChange={handleNewLocationChange}
                              placeholder="Enter location"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={addingLocation}
                            />
                            {locationFormErrors.location && (
                              <p className="text-xs text-red-600 mt-1">{locationFormErrors.location}</p>
                            )}
                          </div>

                          {/* Location Code */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Code <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="location_code"
                              value={newLocationData.location_code}
                              onChange={handleNewLocationChange}
                              placeholder="e.g., MU"
                              maxLength="10"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm uppercase"
                              disabled={addingLocation}
                            />
                            {locationFormErrors.location_code && (
                              <p className="text-xs text-red-600 mt-1">{locationFormErrors.location_code}</p>
                            )}
                          </div>

                          <StateSelectField
                            label="State"
                            required
                            disabled={addingLocation}
                            value={newLocationData.state}
                            onChange={(state) =>
                              setNewLocationData((prev) => ({ ...prev, state }))
                            }
                          />
                          {locationFormErrors.state && (
                            <p className="text-xs text-red-600 col-span-2 sm:col-span-1">{locationFormErrors.state}</p>
                          )}

                          {/* Destination Type */}
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Type <span className="text-red-500">*</span>
                            </label>
                            <SelectField variant="form"
                              name="destination_type"
                              value={newLocationData.destination_type}
                              onChange={handleNewLocationChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={addingLocation}
                            >
                              <option value="branch_destination">Branch</option>
                              <option value="hub_destination">Hub</option>
                            </SelectField>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Contact Number <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              name="location_hub_contact"
                              value={newLocationData.location_hub_contact}
                              onChange={handleNewLocationChange}
                              placeholder="Enter 10 digit number"
                              maxLength="10"
                              inputMode="numeric"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              disabled={addingLocation}
                            />
                            {locationFormErrors.location_hub_contact && (
                              <p className="text-xs text-red-600 mt-1">{locationFormErrors.location_hub_contact}</p>
                            )}
                          </div>
                        </div>

                        {(locationFormErrors.company || locationFormErrors.submit) && (
                          <p className="text-xs text-red-600 mt-2">
                            {locationFormErrors.company || locationFormErrors.submit}
                          </p>
                        )}
                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-4">
                          <Button
                            size="sm"
                            onClick={handleAddLocation}
                            loading={addingLocation}
                          >
                            {addingLocation ? 'Adding...' : 'Add Location'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={cancelAddLocation}
                            disabled={addingLocation}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section 3: Document Information */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Document Information
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Document Type <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="document_type"
                          value={formData.document_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          {documentTypes.map((type) => (
                            <option key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Document Number <span className="text-danger font-bold">*</span>
                        </label>
                        <input
                          type="text"
                          name="document_number"
                          value={formData.document_number}
                          onChange={handleChange}
                          placeholder="Enter document number"
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.document_number ? 'border-red-400' : 'border-gray-300'}`}
                          required
                        />
                        <ValidationMessage error={errors.document_number} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Profile Photo
                        </label>
                        <MediaCaptureField mode="photo" onPick={handleProfilePhotoChange} />
                        {profilePhotoPreview ? (
                          <img
                            src={profilePhotoPreview}
                            alt="Preview"
                            className="mt-2 w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                        ) : null}
                        <p className="text-xs text-gray-600 mt-1">Max 1MB</p>
                        {photoError && <p className="text-xs text-red-600 mt-1">{photoError}</p>}
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Document Attachment
                        </label>
                        <MediaCaptureField mode="document" onPick={handleDocumentAttachmentChange} />
                        {documentPreview === 'pdf' ? (
                          <p className="text-xs text-gray-500 mt-1">PDF attached</p>
                        ) : documentPreview ? (
                          <img
                            src={documentPreview}
                            alt="Preview"
                            className="mt-2 w-16 h-16 object-cover rounded-lg border border-gray-200"
                          />
                        ) : null}
                        <p className="text-xs text-gray-600 mt-1">
                          Upload document image (Max 2MB)
                        </p>
                        {documentError && <p className="text-xs text-red-600 mt-1">{documentError}</p>}
                      </div>
                    </div>
                  </div>

                  {/* Driving License Section - Only for Driver role */}
                  {formData.role_id && roles.find(r => r.id === parseInt(formData.role_id))?.role_name === 'Driver' && (
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 sm:gap-6 mt-4">
                      <div className="sm:col-span-1">
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Driving License Attachment
                        </label>
                        <MediaCaptureField mode="photo" onPick={handleDrivingLicenseChange} />
                        <p className="text-xs text-gray-600 mt-1">Upload driving license (Max 2MB)</p>
                        {licenseError && <p className="text-xs text-red-600 mt-1">{licenseError}</p>}
                        {drivingLicensePreview && (
                          <div className="mt-3">
                            <div className="relative inline-block">
                              <img
                                src={drivingLicensePreview}
                                alt="License Preview"
                                className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-gray-300 rounded-md"
                              />
                              <button
                                type="button"
                                onClick={removeDrivingLicense}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-sm"
                                title="Remove license"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Section 4: Role and Authentication */}
                  <div>
                    <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Role and Authentication
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-6">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Role <span className="text-danger font-bold">*</span>
                        </label>
                        <SelectField variant="form"
                          name="role_id"
                          value={formData.role_id}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          required
                        >
                          <option value="">Select Role</option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.role_name}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      {formData.role_id && PARTY_LINKED_ROLES.includes(
                        roles.find((r) => String(r.id) === String(formData.role_id))?.role_name
                      ) && (
                        <AgentPartySelect
                          value={formData.party_id}
                          onChange={handleChange}
                          companyId={formData.company_id}
                          roleName={
                            roles.find((r) => String(r.id) === String(formData.role_id))?.role_name
                          }
                        />
                      )}

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Password <span className="text-danger font-bold">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="Enter password"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base pr-10 ${errors.password ? 'border-red-400' : 'border-gray-300'}`}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? '👁️' : '👁️‍🗨️'}
                          </button>
                        </div>
                        <ValidationMessage error={errors.password} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Salary Type
                        </label>
                        <SelectField variant="form"
                          name="salary_type"
                          value={formData.salary_type}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {SALARY_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          {getSalaryAmountLabel(formData.salary_type)}
                        </label>
                        <input
                          type="text"
                          name="monthly_salary"
                          value={formData.monthly_salary}
                          onChange={handleChange}
                          placeholder={getSalaryAmountPlaceholder(formData.salary_type)}
                          className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${errors.monthly_salary ? 'border-red-400' : 'border-gray-300'}`}
                        />
                        <ValidationMessage error={errors.monthly_salary} />
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Status
                        </label>
                        <SelectField variant="form"
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        >
                          {userStatuses.map((status) => (
                            <option key={status} value={status}>
                              {status.toUpperCase()}
                            </option>
                          ))}
                        </SelectField>
                      </div>

                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Booking Commission (%)
                        </label>
                        <input
                          type="number"
                          name="booking_commission"
                          value={formData.booking_commission}
                          onChange={handleChange}
                          placeholder="0"
                          min="0"
                          max="100"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                          Delivery Commission (%)
                        </label>
                        <input
                          type="number"
                          name="delivery_commission"
                          value={formData.delivery_commission}
                          onChange={handleChange}
                          placeholder="0"
                          min="0"
                          max="100"
                          className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                        />
                      </div>
                      <AgentInternalFlags
                        visible={
                          roles.find((r) => String(r.id) === String(formData.role_id))?.role_name === 'Agent'
                          && (
                            currentUser?.role === 'System Admin'
                              ? companies.find((c) => String(c.id) === String(formData.company_id))?.network_mode !== 'EXTERNAL'
                              : currentUser?.network_mode !== 'EXTERNAL'
                          )
                        }
                        formData={formData}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* Email Notification */}
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="sendEmail"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
                    />
                    <label htmlFor="sendEmail" className="text-xs sm:text-sm text-gray-900">
                      Send login credentials to user's email
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <Button type="submit" loading={loading}>
                      {loading ? 'Creating...' : 'Create User'}
                    </Button>
                    <Button variant="secondary" onClick={() => navigate('/users')}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
    </DashboardLayout>
  );
};

export default AddUser;